import { RefObject, useRef } from 'react';
import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd';

import { FeedQuestionDetailsFragment } from '../../../../graphql/gen/types';

type DNDType = { type: string; faqId: string; index: number };

export const useDragAndDrop = (
  question: FeedQuestionDetailsFragment,
  boardId: string,
  index: number,
  canDrag: boolean,
  onDrop: (question: FeedQuestionDetailsFragment, atIndex: number) => void,
  onDragEnd: () => void,
  moveCard: (dragIndex: number, hoverIndex: number) => void
): {
  opacity: number;
  ref: RefObject<HTMLDivElement>;
} => {
  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop({
    accept: `faq_${boardId}`, // allow questions to be moved only inside a board, not board to board
    drop: (item: DNDType) => {
      onDrop(question, index);
    },
    hover: (item: DNDType, monitor: DropTargetMonitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // move the preview of the card
      moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const dragObject: DNDType = { type: `faq_${boardId}`, faqId: question.id, index };
  const [{ opacity }, drag] = useDrag({
    item: dragObject,
    collect: (monitor) => ({
      opacity: monitor.isDragging() ? 0.4 : 1,
    }),
    canDrag,
    end: onDragEnd,
  });

  // the actual drag and drop functionality
  drag(drop(ref));
  return { opacity, ref };
};
